// ─── SelectionFrame ────────────────────────────────────────────────────────
// Marco de selección reutilizable para clips dentro del preview del editor.
// Renderiza:
//   • un borde amarillo (brand-primary) alrededor del bounding box de su
//     contenedor (`inset: 0` — el caller posiciona el wrapper),
//   • cuatro corner-dots circulares + cuatro píldoras en cada lado, a modo
//     de manijas de resize,
//   • cada manija captura pointer events y emite callbacks con el delta
//     en pixels desde donde arrancó el drag, junto con el id del handle.
//
// Es agnóstico de la geometría real del clip: el caller decide qué
// significa "resize" (cambiar `widthPct` de un texto, `animScale` de una
// animación, etc.) y aplica la matemática correspondiente. SelectionFrame
// solo se ocupa de la UI y de propagar gestos.

import React, { useCallback, useEffect, useRef, useState } from 'react';

export type SelectionHandle =
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'ne'
  | 'nw'
  | 'se'
  | 'sw';

export type SelectionFramePhase = 'start' | 'move' | 'end';

export type SelectionFrameProps = {
  /** Cuáles manijas mostrar. Default: las 8 (4 esquinas + 4 lados).
   *  Para texto típicamente se omiten 'n' y 's' porque ahí no aporta:
   *  el alto se ajusta automáticamente al contenido. */
  handles?: SelectionHandle[];
  /** Callback unificado para drag de cualquier manija. dx/dy son deltas
   *  en pixels respecto al pointerdown. `phase` permite distinguir el
   *  inicio (commitar valor base), el move continuo y el final (típico
   *  para hacer un único `setClips` definitivo). */
  onResize?: (
    handle: SelectionHandle,
    dx: number,
    dy: number,
    phase: SelectionFramePhase,
  ) => void;
  /** Handle de rotación (círculo arriba del marco). Recibe la posición ABSOLUTA
   *  del puntero; el caller calcula el ángulo respecto del centro del elemento.
   *  Si no se pasa, no se muestra el handle. */
  onRotate?: (clientX: number, clientY: number, phase: SelectionFramePhase) => void;
  /** Color del marco. Default: amarillo brand-primary. */
  color?: string;
  /** Mostrar solo el borde sin manijas (para clips no editables). */
  outlineOnly?: boolean;
  /** true en touch: muestra un CÍRCULO de rotación visible (en vez de la zona +
   *  cursor de escritorio) y agranda las manijas para el dedo. */
  mobile?: boolean;
  /** Muestra el círculo de rotación VISIBLE en ese lado, también en escritorio.
   *  El encuadre de video lo usa porque girar es una de sus tres acciones
   *  principales y tiene que verse que existe. Ojo: donde este círculo está, es
   *  el ÚNICO lugar desde donde se gira — las esquinas quedan sólo de resize.
   *  Sin él (y en escritorio) sigue andando la zona invisible de las esquinas. */
  /**
   * Dónde va el círculo de girar:
   *  - `arriba`  → colgado por FUERA del borde superior (lo del encuadre).
   *  - `adentro` → pegado por DENTRO del borde superior. Para cuando el objeto
   *    llega al borde del escenario y afuera no hay lugar: el círculo quedaba
   *    tapado por lo que hubiera arriba y el toque se lo comía otro elemento.
   *  - `abajo`   → por fuera del borde inferior (el default histórico y el de
   *    touch, donde el dedo no lo tapa).
   */
  circuloDeRotacion?: 'arriba' | 'abajo' | 'adentro';
};

const ALL_HANDLES: SelectionHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const CURSOR: Record<SelectionHandle, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

// Posiciones de cada handle como % sobre el bounding box. El wrapper
// del handle se centra en este punto (`translate(-50%,-50%)`).
const POS: Record<SelectionHandle, { left: string; top: string }> = {
  nw: { left: '0%', top: '0%' },
  n: { left: '50%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  e: { left: '100%', top: '50%' },
  se: { left: '100%', top: '100%' },
  s: { left: '50%', top: '100%' },
  sw: { left: '0%', top: '100%' },
  w: { left: '0%', top: '50%' },
};

const isCorner = (h: SelectionHandle): boolean => h.length === 2;

/** Distancia entre el borde del marco y el CENTRO del círculo de rotación.
 *  Se exporta porque quien dibuja el marco tiene que reservar ese aire: el
 *  círculo vive por FUERA del objeto y si el contenedor no le deja lugar,
 *  queda tapado por lo que haya arriba y el click se lo come otro elemento.
 *  36 y no 20: pegado al marco se confunde con las manijas de esquina y es
 *  difícil agarrar una sin rozar la otra. */
export const SEPARACION_ROTACION = 36;

/** Cuánto se agranda, en cada eje, la zona sensible de una manija respecto de
 *  su dibujo, y piso mínimo de esa zona. El mínimo de 24px es el tamaño de
 *  objetivo que se recomienda para el dedo, y de paso le saca puntería al
 *  mouse: la manija de un lado mide 7px de ancho. */
const AREA_EXTRA = 16;
const AREA_MINIMA = 24;

// Cursor de rotación: UNA sola línea curva (arco) con una flecha en cada extremo
// apuntando a lados opuestos (← arco →), arqueada como abrazando la esquina que
// rota. Contorno blanco para verse sobre cualquier fondo.
const ROT_PATHS =
  "<path d='M3 14 Q12 6.5 21 14'/>" + // arco (la línea)
  "<path d='M3 14 L7 13 M3 14 L5.5 17.6'/>" + // punta izquierda
  "<path d='M21 14 L17 13 M21 14 L18.5 17.6'/>"; // punta derecha
// Genera el cursor con el arco ROTADO `deg` grados, para que abrace la esquina.
function rotCursor(deg: number): string {
  const svg =
    "<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' viewBox='0 0 24 24' fill='none' stroke-linecap='round' stroke-linejoin='round'>" +
    `<g transform='rotate(${deg} 12 12)'>` +
    `<g stroke='white' stroke-width='3.5'>${ROT_PATHS}</g>` +
    `<g stroke='black' stroke-width='1.8'>${ROT_PATHS}</g>` +
    '</g></svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 13 13, grab`;
}
// Un cursor por esquina: el arco (concavidad hacia abajo en 0°) se orienta hacia
// la esquina para que la abrace.
const ROT_CURSOR: Record<string, string> = {
  nw: rotCursor(-45),
  ne: rotCursor(45),
  se: rotCursor(135),
  sw: rotCursor(225),
};

export const SelectionFrame: React.FC<SelectionFrameProps> = ({
  handles = ALL_HANDLES,
  onResize,
  onRotate,
  color = '#FACC15', // amarillo brand-primary (~tailwind yellow-400)
  outlineOnly = false,
  mobile = false,
  circuloDeRotacion,
}) => {
  // ¿El marco muestra un control de rotación VISIBLE? (el círculo con el icono
  // de girar: siempre en touch, y en escritorio cuando el caller lo pide). Si
  // lo hay, girar se hace SOLO desde ahí: las esquinas quedan puras de resize.
  // Antes las esquinas también giraban desde una zona invisible por fuera, y
  // con el círculo a la vista eso es una segunda forma de hacer lo mismo que
  // nadie ve y que se dispara sin querer al ir a agarrar la manija.
  const rotacionALaVista = !!onRotate && (mobile || !!circuloDeRotacion);
  // ── Gestos ───────────────────────────────────────────────────────────────
  // Los listeners del arrastre viven en `window`, NO en la manija, y el gesto
  // se cierra ante cualquier señal de que terminó. Antes estaban en el propio
  // elemento y dependían de `setPointerCapture` (envuelto en un try/catch
  // mudo): si el capture fallaba, si la manija se desmontaba a mitad del
  // arrastre, o si se soltaba el botón fuera de la ventana, el `pointerup`
  // nunca llegaba y el gesto quedaba PEGADO — el clip seguía escalándose o
  // girando con el mouse suelto, hasta el siguiente click.
  //
  // Con esto, un gesto termina por cualquiera de estos caminos:
  //   · `pointerup` / `pointercancel` en cualquier lugar de la ventana;
  //   · un `pointermove` con `buttons === 0` — así se detecta el botón soltado
  //     AFUERA de la ventana, que no manda ningún `pointerup`;
  //   · la ventana pierde el foco (alt-tab con el botón apretado);
  //   · el componente se desmonta (el clip dejó de estar seleccionado o de
  //     estar activo en el tiempo mientras arrastrabas).
  const ROT_THRESHOLD = 4;

  type Gesto = {
    tipo: 'resize' | 'rotar';
    handle: SelectionHandle;
    pointerId: number;
    startX: number;
    startY: number;
    ultimoX: number;
    ultimoY: number;
    /** Sólo rotación: el umbral ya se superó y el gesto empezó de verdad. */
    activo: boolean;
    cursor: string;
  };

  const gestoRef = useRef<Gesto | null>(null);
  // Sólo sirve para montar/desmontar los listeners globales: el valor en sí no
  // se dibuja. El estado del gesto vive en el ref para no re-renderizar en cada
  // pointermove.
  const [hayGesto, setHayGesto] = useState(false);

  // Los callbacks se leen por ref para que el efecto de los listeners no se
  // tenga que re-suscribir cada vez que el padre re-renderiza (pasa en CADA
  // move, porque el padre está actualizando el clip).
  const onResizeRef = useRef(onResize);
  const onRotateRef = useRef(onRotate);
  onResizeRef.current = onResize;
  onRotateRef.current = onRotate;

  const terminarGesto = useCallback((e: PointerEvent | null) => {
    const g = gestoRef.current;
    gestoRef.current = null;
    setHayGesto(false);
    document.body.style.cursor = '';
    if (!g) return;
    // Sin evento (desmontaje o blur) se cierra con la última posición vista,
    // para que el caller reciba SIEMPRE su 'end' y limpie lo que tenga.
    const x = e ? e.clientX : g.ultimoX;
    const y = e ? e.clientY : g.ultimoY;
    if (g.tipo === 'resize') {
      onResizeRef.current?.(g.handle, x - g.startX, y - g.startY, 'end');
    } else if (g.activo) {
      onRotateRef.current?.(x, y, 'end');
    }
  }, []);

  useEffect(() => {
    if (!hayGesto) return;

    const alMover = (e: PointerEvent) => {
      const g = gestoRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      // Botón soltado afuera de la ventana: no hubo `pointerup`, y si no
      // cortamos acá el clip sigue al mouse para siempre.
      if (e.buttons === 0) {
        terminarGesto(e);
        return;
      }
      g.ultimoX = e.clientX;
      g.ultimoY = e.clientY;

      if (g.tipo === 'resize') {
        onResizeRef.current?.(g.handle, e.clientX - g.startX, e.clientY - g.startY, 'move');
        return;
      }
      // Rotación: no empieza hasta superar el umbral, así un tap suelto sobre
      // la manija no mueve el clip ni un grado.
      if (!g.activo) {
        if (Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < ROT_THRESHOLD) return;
        g.activo = true;
        onRotateRef.current?.(e.clientX, e.clientY, 'start');
        return;
      }
      onRotateRef.current?.(e.clientX, e.clientY, 'move');
    };

    const alSoltar = (e: PointerEvent) => {
      const g = gestoRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      terminarGesto(e);
    };
    const alPerderElFoco = () => terminarGesto(null);

    window.addEventListener('pointermove', alMover);
    window.addEventListener('pointerup', alSoltar);
    window.addEventListener('pointercancel', alSoltar);
    window.addEventListener('blur', alPerderElFoco);
    return () => {
      window.removeEventListener('pointermove', alMover);
      window.removeEventListener('pointerup', alSoltar);
      window.removeEventListener('pointercancel', alSoltar);
      window.removeEventListener('blur', alPerderElFoco);
    };
  }, [hayGesto, terminarGesto]);

  // Si el marco desaparece a mitad del arrastre (se deseleccionó el clip, o el
  // cursor de la timeline se salió de su rango), el gesto se cierra igual.
  useEffect(() => {
    return () => {
      if (gestoRef.current) terminarGesto(null);
    };
  }, [terminarGesto]);

  const empezarGesto = (
    tipo: 'resize' | 'rotar',
    handle: SelectionHandle,
    cursor: string,
  ) => (e: React.PointerEvent) => {
    // Sólo el botón principal arrastra: con el secundario se abre el menú
    // contextual y nunca llega un `pointerup` que cierre el gesto.
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    gestoRef.current = {
      tipo,
      handle,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      ultimoX: e.clientX,
      ultimoY: e.clientY,
      activo: false,
      cursor,
    };
    setHayGesto(true);
    // El cursor se fija en el body mientras dura el gesto. Sin esto (y sin
    // pointer capture, que se sacó por frágil) el puntero iría cambiando de
    // ícono al pasar por encima del video y de los paneles.
    document.body.style.cursor = cursor;
    if (tipo === 'resize') onResizeRef.current?.(handle, 0, 0, 'start');
  };

  return (
    <div
      // Borde alrededor del contenedor padre. `pointer-events-none` aquí
      // para que el contenedor (drag) siga recibiendo eventos cuando el
      // puntero pasa por encima de la línea del borde — solo las manijas
      // re-activan eventos.
      className="absolute inset-0 pointer-events-none"
      style={{
        outline: `1.5px solid ${color}`,
        outlineOffset: 0,
        // Sin background ni border para que el contenido interno se
        // siga viendo nítido — solo el outline.
      }}
    >
      {!outlineOnly &&
        handles.map(h => {
          const corner = isCorner(h);
          // Tamaños (más grandes en mobile para el dedo):
          //   • esquinas → círculo · lados N/S → píldora horizontal · E/W → vertical
          const dot = mobile ? 16 : 11;
          const long = mobile ? 26 : 20;
          const short = mobile ? 10 : 7;
          const w = corner ? dot : h === 'n' || h === 's' ? long : short;
          const ht = corner ? dot : h === 'n' || h === 's' ? short : long;
          const pos = POS[h];
          // Dirección diagonal hacia AFUERA de la esquina (para la zona de rotación).
          const rotDirX = h.includes('w') ? -1 : 1;
          const rotDirY = h.includes('n') ? -1 : 1;
          return (
            <React.Fragment key={h}>
              {/* Zona de rotación por fuera de la esquina, con cursor de flecha.
                  Es el camino de FALLBACK: sale sólo cuando el marco no tiene
                  el círculo de girar a la vista (hoy, el editor gráfico en
                  escritorio). Donde el círculo está, gira sólo el círculo. */}
              {corner && onRotate && !rotacionALaVista && (
                <div
                  onPointerDown={empezarGesto('rotar', h, ROT_CURSOR[h] ?? rotCursor(0))}
                  className="absolute pointer-events-auto"
                  style={{
                    left: pos.left,
                    top: pos.top,
                    width: 26,
                    height: 26,
                    transform: `translate(calc(-50% + ${rotDirX * 15}px), calc(-50% + ${rotDirY * 15}px))`,
                    cursor: ROT_CURSOR[h] ?? rotCursor(0),
                    touchAction: 'none',
                  }}
                />
              )}
              {/* El ÁREA que recibe el click es más grande que la manija que
                  se ve: agarrar un punto de 11px pide puntería, y agrandar el
                  dibujo para arreglarlo taparía el clip justo en el borde que
                  se está tratando de ajustar. Así que el div de afuera es una
                  zona invisible de al menos AREA_MINIMA, y adentro va la manija
                  con su tamaño de siempre. */}
              <div
                onPointerDown={empezarGesto('resize', h, CURSOR[h])}
                className="absolute pointer-events-auto"
                style={{
                  left: pos.left,
                  top: pos.top,
                  width: Math.max(w + AREA_EXTRA, AREA_MINIMA),
                  height: Math.max(ht + AREA_EXTRA, AREA_MINIMA),
                  transform: 'translate(-50%, -50%)',
                  cursor: CURSOR[h],
                  touchAction: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: w,
                    height: ht,
                    transform: 'translate(-50%, -50%)',
                    background: color,
                    border: `1.5px solid ${color}`,
                    borderRadius: corner ? '50%' : 999,
                    // Los eventos los toma el área de afuera: si la manija los
                    // atajara, el borde del área quedaría muerto.
                    pointerEvents: 'none',
                    // Sombra sutil para que el handle se despegue del contenido del
                    // clip (texto / imagen) cuando el fondo es de color similar.
                    boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                  }}
                />
              </div>
            </React.Fragment>
          );
        })}

      {/* Círculo de rotación visible y arrastrable. En touch sale siempre (el
          dedo no tiene cursor que anticipe la zona de las esquinas); en
          escritorio, solo si el caller lo pide con `circuloDeRotacion`. */}
      {(mobile || circuloDeRotacion) && onRotate && (
        <div
          onPointerDown={empezarGesto('rotar', 'n', 'pointer')}
          className="absolute pointer-events-auto flex items-center justify-center"
          style={{
            left: '50%',
            // `arriba` lo cuelga por encima del borde superior (lo de la maqueta
            // del encuadre), `adentro` lo mete justo por debajo de ese mismo
            // borde —para cuando afuera no hay lugar— y el default histórico
            // —y el de touch— es abajo, donde no queda tapado por el dedo.
            top:
              circuloDeRotacion === 'arriba' || circuloDeRotacion === 'adentro'
                ? '0%'
                : '100%',
            marginTop: circuloDeRotacion === 'arriba' ? -SEPARACION_ROTACION : SEPARACION_ROTACION,
            width: 34,
            height: 34,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            background: '#fff',
            border: `1.5px solid ${color}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            // Se agarra y se gira: `pointer` avisa que es un control aparte y no
            // otra manija de resize (las de esquina/lado tienen su cursor de
            // flecha). Durante el arrastre el puntero queda capturado, así que
            // este cursor es el que se ve todo el gesto.
            cursor: 'pointer',
            touchAction: 'none',
          }}
        >
          {/* Ícono de girar: un arco CIRCULAR de casi vuelta entera con una
              punta de flecha maciza. Antes era el mismo dibujo que el cursor
              —un arco chato con dos puntitas de trazo— que a 18px se leía como
              una sonrisa y no decía "esto gira". El arco cerrado con una sola
              punta es el gesto que hace el control: da la vuelta alrededor de
              un centro, en un sentido. */}
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2.1"
            strokeLinecap="round"
            aria-hidden
          >
            {/* Arco abierto arriba a la derecha, dejando lugar a la flecha. */}
            <path d="M20.5 12a8.5 8.5 0 1 1-2.49-6.01" />
            {/* Punta maciza, apoyada en el extremo del arco y apuntando en el
                sentido del giro (horario). */}
            <path
              d="M18.4 1.9 L19.1 6.9 L14.1 6.2 Z"
              fill={color}
              stroke="none"
            />
          </svg>
        </div>
      )}
    </div>
  );
};
